/**
 * The tail of each record's tag-write chain, keyed by resource and record id.
 *
 * Module scope rather than component state because the controls that write
 * tags come and go while their writes are still in flight — a selection bar
 * unmounts the moment the selection is cleared, and virtualized rows unmount
 * on scroll. A queue owned by one of them would let the next one start an
 * unordered write.
 */
const writeChainByRecord = new Map<string, Promise<unknown>>()

/**
 * Runs `write` after every tag write already queued for the same record.
 *
 * The tag endpoints replace a record's whole list, so two writes in flight at
 * once race, and the one the service handles last wins — an older list can
 * come back over a newer edit. Chaining orders them per record while leaving
 * different records to go in parallel. A rejected write does not stop the ones
 * behind it; the returned promise still rejects for the caller that queued it.
 *
 * Ids are only unique within their own resource, so `resource` — a fixed name
 * like `"agents"`, never an id — joins the id to make the chain key.
 */
export function enqueueTagWrite<T>(
  resource: string,
  recordId: string,
  write: () => Promise<T>,
): Promise<T> {
  const key = `${resource}/${recordId}`
  const previousWrite = writeChainByRecord.get(key) ?? Promise.resolve()
  const result = previousWrite.catch(() => undefined).then(write)

  const tail: Promise<void> = result
    .catch(() => undefined)
    .then(() => {
      // Nothing queued behind this write, so the record needs no chain until
      // it is written again.
      if (writeChainByRecord.get(key) === tail) {
        writeChainByRecord.delete(key)
      }
    })

  writeChainByRecord.set(key, tail)

  return result
}
